import {
    Button,
    Chip,
    Field,
    Label,
    Search,
    EXPERIMENTAL_Suggestion as Suggestion,
    type SuggestionItem,
    Tooltip,
} from "@digdir/designsystemet-react";
import { ArrowForwardIcon, DownloadIcon, FileCsvIcon, FileExcelIcon } from "@navikt/aksel-icons";
import { useEffect, useState } from "react";
import { FlexContainer } from "../../shared/FlexContainer";
import type { FilterKey } from "./filterDefinitions";
import { filterKeys } from "./filterDefinitions";
import { GroupedSuggestionFilter } from "./GroupedSuggestionFilter";
import type { GroupedSuggestionConfig } from "./groupedSuggestion";
import { MultipleSuggestionOptionContent } from "./MultipleSuggestionOptionContent";
import { YearSlider } from "./YearSlider";
import "./Filter.css";
import { InfoTooltip } from "../../shared/InfoTooltip";

type YearRange = {
    fromYear: number | null;
    toYear: number | null;
};

type FilterProps = {
    searchTerm: string;
    selectedFilters: Record<FilterKey, SuggestionItem[]>;
    filterOptions: Record<FilterKey, SuggestionItem[]>;
    groupedFilterConfigByKey: Partial<Record<FilterKey, GroupedSuggestionConfig>>;
    minAvailableYear: number;
    maxAvailableYear: number;
    yearRange: YearRange;
    onSearchTermChange: (nextSearchTerm: string) => void;
    onFilterChange: (key: FilterKey, values: SuggestionItem[]) => void;
    onYearRangeChange: (nextRange: { fromYear: number; toYear: number }) => void;
    onClearAllFilters: () => void;
    onDownloadCsv: () => void;
    onDownloadExcel: () => void;
};

export const Filter = ({
    searchTerm,
    selectedFilters,
    filterOptions,
    groupedFilterConfigByKey,
    minAvailableYear,
    maxAvailableYear,
    yearRange,
    onSearchTermChange,
    onFilterChange,
    onYearRangeChange,
    onClearAllFilters,
    onDownloadCsv,
    onDownloadExcel,
}: FilterProps) => {
    const filterTooltipTextByKey: Partial<Record<FilterKey, string>> = {
        Støtteobjekt:
            "Støtteobjekt betyr hva støtten gis til, som bygg, kjøretøy eller anlegg. Her vises de typene vi får flest spørsmål om, selv om Enova støtter flere.",
        Avtaleperiode:
            "Enovas avtaleperiode er tidsrommet en styringsavtale med staten gjelder for. Styringsavtalen er Enovas viktigste styringsdokument og fornyes hvert fjerde år.",
        Status: "Innvilget: Prosjektet har fått tilsagn om støtte, men er ikke igangsatt. Under gjennomføring: Prosjektet er igangsatt, men ikke ferdigstilt. Ferdigstilt: Prosjektet er ferdigstilt og støtten er utbetalt. Kansellert: Prosjektet ble avbrutt eller ikke igangsatt.",
    };

    const [shareStatus, setShareStatus] = useState<"idle" | "shared" | "copied" | "error">("idle");

    useEffect(() => {
        if (shareStatus === "idle") {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShareStatus("idle");
        }, 2500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [shareStatus]);

    const copyWithFallback = (text: string): boolean => {
        if (typeof document === "undefined") {
            return false;
        }

        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand("copy");
        textArea.remove();
        return success;
    };

    const handleShareUrl = async () => {
        if (typeof window === "undefined") {
            return;
        }

        const currentUrl = window.location.href;

        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
            try {
                await navigator.share({
                    title: "Kunnskapshuset",
                    text: "Del filtrert utvalg",
                    url: currentUrl,
                });
                setShareStatus("shared");
                return;
            } catch (error) {
                if (error instanceof DOMException && error.name === "AbortError") {
                    setShareStatus("idle");
                    return;
                }
            }
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(currentUrl);
                setShareStatus("copied");
                return;
            }

            setShareStatus(copyWithFallback(currentUrl) ? "copied" : "error");
        } catch {
            setShareStatus(copyWithFallback(currentUrl) ? "copied" : "error");
        }
    };

    const shareTooltipContent =
        shareStatus === "shared"
            ? "Lenke delt"
            : shareStatus === "copied"
              ? "Lenke kopiert"
              : "Del prosjektlisten. Hvis du har valgt filtre, vil en ferdigfiltret lenke bli delt.";
    const shareStatusText =
        shareStatus === "shared"
            ? "Lenke delt"
            : shareStatus === "copied"
              ? "Lenke kopiert"
              : shareStatus === "error"
                ? "Kunne ikke kopiere lenke"
                : "";

    const activeFilterItems = filterKeys.flatMap((key) =>
        selectedFilters[key].map((item) => ({
            key,
            value: item.value,
            label: item.label,
        })),
    );
    const hasYearFilter =
        yearRange.fromYear !== null &&
        yearRange.toYear !== null &&
        (yearRange.fromYear !== minAvailableYear || yearRange.toYear !== maxAvailableYear);
    const yearFilterLabel =
        yearRange.fromYear !== null && yearRange.toYear !== null ? `${yearRange.fromYear}-${yearRange.toYear}` : "-";
    const activeFilterCount = activeFilterItems.length + (hasYearFilter ? 1 : 0);

    return (
        <section style={{ width: "100%" }} className="prosjektliste-filter">
            <FlexContainer marginTop="var(--ds-size-4)" alignItems="flex-start" gap="var(--ds-size-3)">
                <Field>
                    <Label>Søk i prosjektlisten</Label>
                    <Search className="prosjektliste-filter-search">
                        <Search.Input
                            aria-label="Søk i prosjektlisten"
                            value={searchTerm}
                            onChange={(event) => {
                                onSearchTermChange(event.target.value);
                            }}
                        />
                        <Search.Clear
                            aria-label="Tøm søk"
                            onClick={() => {
                                onSearchTermChange("");
                            }}
                        />
                    </Search>
                </Field>

                {filterKeys.map((key) =>
                    groupedFilterConfigByKey[key] ? (
                        <GroupedSuggestionFilter
                            key={key}
                            className="prosjektliste-filter-suggestion"
                            label={key}
                            selected={selectedFilters[key]}
                            onSelectedChange={(value) => {
                                onFilterChange(key, value);
                            }}
                            config={groupedFilterConfigByKey[key] as GroupedSuggestionConfig}
                            tooltipText={filterTooltipTextByKey[key]}
                        />
                    ) : (
                        <Field key={key}>
                            <FlexContainer width="100%" gap="var(--ds-size-2)">
                                <Label>{key}</Label>
                                {filterTooltipTextByKey[key] && (
                                    <InfoTooltip text={filterTooltipTextByKey[key]} withBg placement="top" />
                                )}
                            </FlexContainer>

                            <Suggestion
                                className="prosjektliste-filter-suggestion"
                                multiple
                                selected={selectedFilters[key]}
                                onSelectedChange={(value) => {
                                    onFilterChange(key, value);
                                }}
                            >
                                <Suggestion.Input
                                    aria-label={key}
                                    placeholder={
                                        selectedFilters[key].length > 0
                                            ? selectedFilters[key].map((item) => item.label).join(", ")
                                            : undefined
                                    }
                                    className={
                                        selectedFilters[key].length > 0 ? "suggestion-input-has-selection" : undefined
                                    }
                                />
                                <Suggestion.Clear />
                                <Suggestion.List>
                                    <Suggestion.Empty>Ingen valg</Suggestion.Empty>
                                    {filterOptions[key].map((option) => (
                                        <Suggestion.Option key={option.value} value={option.value}>
                                            <MultipleSuggestionOptionContent
                                                label={option.label}
                                                isSelected={selectedFilters[key].some(
                                                    (selectedOption) => selectedOption.value === option.value,
                                                )}
                                            />
                                        </Suggestion.Option>
                                    ))}
                                </Suggestion.List>
                            </Suggestion>
                        </Field>
                    ),
                )}

                <YearSlider
                    minYear={minAvailableYear}
                    maxYear={maxAvailableYear}
                    fromYear={yearRange.fromYear}
                    toYear={yearRange.toYear}
                    onChange={onYearRangeChange}
                />

                <FlexContainer
                    flex="1"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    gap="var(--ds-size-1)"
                    flexWrap="nowrap"
                    flexDirection="column"
                >
                    <FlexContainer gap="var(--ds-size-1)" flexWrap="nowrap" padding="var(--ds-size-4) 0 0">
                        <Tooltip
                            content="Last ned data som en CSV-fil. Også filtreringsparametre, som f.eks. sektor og støtteprogram, vil være inkludert i nedlastingen."
                            placement="top"
                        >
                            <Button
                                variant="tertiary"
                                icon
                                aria-label="Last ned filtrert utvalg som CSV"
                                onClick={onDownloadCsv}
                                style={{ position: "relative" }}
                            >
                                <DownloadIcon aria-hidden={true} />
                                <FileCsvIcon
                                    aria-hidden={true}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        width: "1.25rem",
                                        height: "1.25rem",
                                    }}
                                />
                            </Button>
                        </Tooltip>

                        <Tooltip
                            content="Last ned data som en Excel-fil. Også filtreringsparametre, som f.eks. sektor og støtteprogram, vil være inkludert i nedlastingen."
                            placement="top"
                        >
                            <Button
                                variant="tertiary"
                                icon
                                aria-label="Last ned filtrert utvalg som Excel"
                                onClick={onDownloadExcel}
                                style={{ position: "relative" }}
                            >
                                <DownloadIcon aria-hidden={true} />
                                <FileExcelIcon
                                    aria-hidden={true}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        width: "1.25rem",
                                        height: "1.25rem",
                                    }}
                                />
                            </Button>
                        </Tooltip>

                        <Tooltip content={shareTooltipContent} placement="top">
                            <Button variant="tertiary" icon aria-label="Del filtrert utvalg" onClick={handleShareUrl}>
                                <ArrowForwardIcon aria-hidden={true} />
                            </Button>
                        </Tooltip>
                    </FlexContainer>

                    <output
                        aria-live="polite"
                        style={{
                            minHeight: "1.25rem",
                            position: "absolute",
                            top: "2.5rem",
                            fontSize: "var(--ds-font-size-2)",
                        }}
                    >
                        {shareStatusText}
                    </output>
                </FlexContainer>
            </FlexContainer>

            {activeFilterCount > 0 && (
                <FlexContainer gap="var(--ds-size-1)" marginTop="var(--ds-size-6)">
                    {hasYearFilter && (
                        <Chip.Removable
                            onClick={() => {
                                onYearRangeChange({ fromYear: minAvailableYear, toYear: maxAvailableYear });
                            }}
                        >
                            Vedtaksår: {yearFilterLabel}
                        </Chip.Removable>
                    )}

                    {activeFilterItems.map((item) => (
                        <Chip.Removable
                            key={`${item.key}-${item.value}`}
                            onClick={() => {
                                onFilterChange(
                                    item.key,
                                    selectedFilters[item.key].filter(
                                        (selectedItem) => selectedItem.value !== item.value,
                                    ),
                                );
                            }}
                        >
                            {item.key}: {item.label}
                        </Chip.Removable>
                    ))}

                    <Chip.Button onClick={onClearAllFilters} data-color="kraftoransje">
                        Nullstill alle filtre
                    </Chip.Button>
                </FlexContainer>
            )}
        </section>
    );
};
